import { useEffect, useState } from "react";
import { Box, Image } from "@chakra-ui/react";
import sunsetData from "../../assets/data/honolulu_sunset_azimuth.json"; // Adjust path as needed
import balconyImage from "../../assets/images/balcony_pano.png";
import DateChanger from "./DateChanger";

const slope = 0.27658228;
const intercept = -44.08417722;
const adjustment = 7.25;

// a function for conterting a direction in degrees -> percent of way across image to put line
function headingToPercent(heading) {
  return slope * heading + intercept + adjustment;
}

// Helper to format date to YYYY-MM-DD
const formatDate = (date) => date.toISOString().split("T")[0];

export default function SunsetLine() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentInfo, setCurrentInfo] = useState(null);
  const [extremaInfo, setExtremaInfo] = useState(null);

  useEffect(() => {
    // Update currentInfo based on currentDate
    const dateStr = formatDate(currentDate);
    const todayData = sunsetData.find((entry) => entry.Date === dateStr);
    if (todayData) {
      const sunsetAzimuth = todayData["Sunset Azimuth"];
      setCurrentInfo({
        azimuth: sunsetAzimuth,
        percent: headingToPercent(sunsetAzimuth),
        date: currentDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
      });
    } else {
      setCurrentInfo(null);
      console.warn(`No data found for ${dateStr}`);
    }

    // Compute extrema only once for the year
    if (!extremaInfo) {
      const yyyy = currentDate.getFullYear();
      const upcomingYearData = sunsetData.filter((entry) =>
        entry.Date.startsWith(`${yyyy}`)
      );
      if (upcomingYearData.length > 0) {
        let minEntry = upcomingYearData[0];
        let maxEntry = upcomingYearData[0];
        upcomingYearData.forEach((entry) => {
          if (entry["Sunset Azimuth"] < minEntry["Sunset Azimuth"])
            minEntry = entry;
          if (entry["Sunset Azimuth"] > maxEntry["Sunset Azimuth"])
            maxEntry = entry;
        });
        setExtremaInfo({
          min: {
            azimuth: minEntry["Sunset Azimuth"],
            percent: headingToPercent(minEntry["Sunset Azimuth"]),
            date: new Date(minEntry.Date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            }),
          },
          max: {
            azimuth: maxEntry["Sunset Azimuth"],
            percent: headingToPercent(maxEntry["Sunset Azimuth"]),
            date: new Date(maxEntry.Date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            }),
          },
        });
      }
    }

    // Keyboard listener
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        setCurrentDate((prev) => {
          const newDate = new Date(prev);
          newDate.setDate(newDate.getDate() - 1);
          return newDate;
        });
      } else if (event.key === "ArrowRight") {
        setCurrentDate((prev) => {
          const newDate = new Date(prev);
          newDate.setDate(newDate.getDate() + 1);
          return newDate;
        });
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentDate, extremaInfo]);

  const changeDate = (days) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + days);
      return newDate;
    });
  };

  return (
    <Box position="relative" width="100%" maxWidth="800px" margin="auto">
      {/* Date Control Buttons */}
      <DateChanger currentInfo={currentInfo} changeDate={changeDate} />

      {/* Image with lines */}
      <Box position="relative" display="inline-block">
        <Image src={balconyImage} alt="Balcony Panoramic" width="100%" />

        {/* Extrema Lines and Labels */}
        {extremaInfo && (
          <>
            {/* Min Line */}
            <Box
              position="absolute"
              top="0"
              height="100%"
              left={`${extremaInfo.min.percent}%`}
              width="2px"
              bg="blue"
            />
            <Box
              position="absolute"
              bottom="0"
              transform="translateX(-100%)"
              left={`${extremaInfo.min.percent}%`}
              textAlign="center"
              fontSize="sm"
              color="blue"
              fontWeight="bold"
              bg="white"
              border="1px solid blue"
              w="60px"
            >
              {extremaInfo.min.date}
            </Box>

            {/* Max Line */}
            <Box
              position="absolute"
              top="0"
              height="100%"
              left={`${extremaInfo.max.percent}%`}
              width="2px"
              bg="blue"
            />
            <Box
              position="absolute"
              bottom="0"
              transform="translateX(3%)"
              left={`${extremaInfo.max.percent}%`}
              textAlign="center"
              fontSize="sm"
              color="blue"
              fontWeight="bold"
              bg="white"
              border="1px solid blue"
              w="60px"
            >
              {extremaInfo.max.date}
            </Box>
          </>
        )}

        {/* Current Line */}
        {currentInfo && (
          <Box
            position="absolute"
            top="0"
            height="100%"
            left={`${currentInfo.percent}%`}
            width="2px"
            bg="red"
          />
        )}
      </Box>
    </Box>
  );
}
